import { useCallback, useEffect, useState } from "react";
import { Button, Flex, Form, Heading, Modal, useToast } from "@ir/ui";
import { UpdatePurchaserConfigTemplateDTO } from "@src/domain/dto/purchasers";
import { usePurchasersData } from "@src/presentation/providers/PurchasersDataProvider";
import { PurchaserConfigTemplateForm } from "./PurchaserConfigTemplateForm";
import {
  EMPTY_CONFIG_TEMPLATE_FORM_VALUES,
  formatJsonForTextarea,
  parseOptionalJsonField,
  parseOptionalNumberField,
  type PurchaserConfigTemplateFormValues,
} from "./purchaserConfigTemplateFormUtils";
import {
  getPurchaserConfigLoadErrorMessage,
  getPurchaserConfigSaveErrorMessage,
} from "./purchaserConfigSaveErrors";
import "./PurchaserConfigTemplateModal.scss";

interface PurchaserConfigTemplateModalProps {
  purchaserId: number | null;
  purchaserName?: string;
  onClose: () => void;
}

export const PurchaserConfigTemplateModal = ({
  purchaserId,
  purchaserName,
  onClose,
}: PurchaserConfigTemplateModalProps) => {
  const { success: successToast, error: errorToast } = useToast();
  const { getPurchaserConfigTemplate, updatePurchaserConfigTemplate } =
    usePurchasersData();
  const [form] = Form.useForm<PurchaserConfigTemplateFormValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);
  const [initialValues, setInitialValues] =
    useState<PurchaserConfigTemplateFormValues>(
      EMPTY_CONFIG_TEMPLATE_FORM_VALUES,
    );

  const isOpen = purchaserId != null;

  useEffect(() => {
    if (purchaserId == null) {
      setInitialValues(EMPTY_CONFIG_TEMPLATE_FORM_VALUES);
      form.resetFields();
      setIsFormSubmittable(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    void (async () => {
      try {
        const template = await getPurchaserConfigTemplate(purchaserId);
        if (cancelled) return;

        const nextValues: PurchaserConfigTemplateFormValues = template
          ? {
              metadata: formatJsonForTextarea(template.metadata),
              searchKeywords: template.searchKeywords ?? "",
              intelligenceFilter: formatJsonForTextarea(
                template.intelligenceFilter,
              ),
              deductionTypeInvoicePatternForDiscount: formatJsonForTextarea(
                template.deductionTypeInvoicePatternForDiscount,
              ),
              additionalFieldMapping: formatJsonForTextarea(
                template.additionalFieldMapping,
              ),
              discountTermAgreementDaysTolerance:
                template.discountTermAgreementDaysTolerance == null
                  ? ""
                  : String(template.discountTermAgreementDaysTolerance),
            }
          : EMPTY_CONFIG_TEMPLATE_FORM_VALUES;

        setInitialValues(nextValues);
        form.setFieldsValue(nextValues);
        setIsFormSubmittable(false);
      } catch (err) {
        if (!cancelled) {
          errorToast({
            content: getPurchaserConfigLoadErrorMessage(err),
          });
          onClose();
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [errorToast, form, getPurchaserConfigTemplate, onClose, purchaserId]);

  const handleSubmit = useCallback(
    async (values: PurchaserConfigTemplateFormValues) => {
      if (purchaserId == null) return;

      setIsSaving(true);
      try {
        const metadata = parseOptionalJsonField(values.metadata, "Metadata");
        const intelligenceFilter = parseOptionalJsonField(
          values.intelligenceFilter,
          "Intelligence Filter",
        );
        const deductionTypeInvoicePatternForDiscount = parseOptionalJsonField(
          values.deductionTypeInvoicePatternForDiscount,
          "Deduction Type Invoice Pattern for Discount",
        );
        const additionalFieldMapping = parseOptionalJsonField(
          values.additionalFieldMapping,
          "Additional Field Mapping",
        );
        const discountTermAgreementDaysTolerance = parseOptionalNumberField(
          values.discountTermAgreementDaysTolerance,
          "Discount Term Agreement Days Tolerance",
        );

        await updatePurchaserConfigTemplate(
          new UpdatePurchaserConfigTemplateDTO({
            purchaserId,
            metadata,
            searchKeywords: values.searchKeywords.trim() || null,
            intelligenceFilter,
            deductionTypeInvoicePatternForDiscount,
            additionalFieldMapping,
            discountTermAgreementDaysTolerance,
            isActive: true,
          }),
        );

        successToast({
          content: `Config template for ${purchaserName ?? "purchaser"} saved successfully`,
        });
        onClose();
      } catch (err) {
        errorToast({
          content: getPurchaserConfigSaveErrorMessage(err),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      errorToast,
      onClose,
      purchaserId,
      purchaserName,
      successToast,
      updatePurchaserConfigTemplate,
    ],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closable
      isFullScreen
      wrapClassName="purchaser-config-template"
      headerContent={
        <Heading level={6} className="text-grey-700 font-bold m-0">
          Config Template
          {purchaserName ? ` — ${purchaserName}` : ""}
        </Heading>
      }
      footerContent={
        <Flex gap={0.5}>
          <Button
            size="small"
            onPress={onClose}
            color="grey"
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            onPress={() => form.submit()}
            isLoading={isSaving}
            isDisabled={isSaving || isLoading || !isFormSubmittable}
          >
            Save
          </Button>
        </Flex>
      }
    >
      {isOpen ? (
        <PurchaserConfigTemplateForm
          form={form}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      ) : null}
    </Modal>
  );
};
